import { initialsFor, useMe } from '../../../lib/me';
import { useProfilePhoto } from '../../../lib/use-profile-photo';
import { useRecipient } from './settings-queries';

// The displayed name prefers the recipient's chosen name (editable in Settings)
// over the raw identity name; initials are derived from whichever wins.
function initialsFromName(name: string, me: ReturnType<typeof useMe>): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
  if (parts.length === 1) return parts[0]![0]!.toUpperCase();
  return initialsFor(me);
}

// The single source of the profile header's identity — name, initials, email, photo —
// shared by SettingsDesktop and SettingsMobile so the two can't derive it
// differently. The photo comes from the same `useProfilePhoto` the rail and drawer
// avatars read, so one person looks the same in all three places.
export function useProfileIdentity(): {
  displayName: string;
  initials: string;
  email: string;
  photoUrl: string | null;
  onPhotoError: () => void;
  /** The recipient's OWN self-photo (Recipient.avatarUrl), empty when unset — the photo editor gates
   *  its Remove item on this, since only a self-photo (not the Wix identity photo) can be cleared here. */
  selfPhotoUrl: string;
} {
  const me = useMe();
  const { data: recipient } = useRecipient();
  // Prefer the recipient's own self-photo (set in Settings) over the Wix identity photo.
  const { photoUrl, onPhotoError } = useProfilePhoto(me, recipient?.avatarUrl);
  const identityName = me ? `${me.firstName ?? ''} ${me.lastName ?? ''}`.trim() : '';
  const displayName = (recipient?.name || identityName).trim();
  return {
    displayName,
    initials: initialsFromName(displayName, me) || '–',
    email: me?.email || me?.userName || '',
    photoUrl,
    onPhotoError,
    selfPhotoUrl: recipient?.avatarUrl?.trim() || '',
  };
}
